import { getCountryResorts, findHotels } from '@/shared/api';
import { useTravelataAppConfig } from '@/shared/composables';
import {
  type Country,
  type Hotel,
  type Resort,
  type SearchFormData,
  type SearchCriteriaParams,
  type DateRange,
  type NightsRange,
  type DepartureCity,
} from '@/shared/model';
import { resolveDefaultCriteria } from './defaultCriteria';
import { toTouristGroup, toTouristRooms } from './touristRooms';
import { DEFAULT_TOURISTS } from '../config/tourists';
import { type SearchCriteria, type Selection, type TouristsSelection } from '../model/types';

export async function buildSearchCriteria(
  params: SearchCriteriaParams | null,
  formData: SearchFormData,
  resortsUrl?: string,
): Promise<SearchCriteria> {
  const { cities, countries } = formData;
  let destination: Selection | null = null;
  let dateRange: DateRange | null = null;
  let nights: NightsRange | null = null;
  let tourists: TouristsSelection | null = null;
  const appConfig = useTravelataAppConfig();

  if (params?.checkInDateRange.from && params.checkInDateRange.to) {
    dateRange = {
      daysFlexible: 0,
      ...params.checkInDateRange,
    };
  }
  if (params?.nightRange.from || params?.nightRange.to) {
    nights = {
      ...params.nightRange,
    };
  }
  if (params?.touristGroup.adults) {
    tourists = { rooms: toTouristRooms(params.touristGroup) };
  }

  const country = countries.find((c) => c.id === params?.countries[0]) || countries[0];
  const city =
    cities.find((c) => c.id === params?.departureCity.id) ||
    cities.find((c) => c.id === appConfig.defaultDepartureCityId);
  const fromCity: DepartureCity | null = city || null;
  const resorts = await getCountryResorts(country, resortsUrl);
  const defaultCriteria = resolveDefaultCriteria(formData.defaultCriteria, country.id, fromCity?.type);

  if (!dateRange) {
    dateRange = defaultCriteria.dateRange || null;
  }
  if (!nights) {
    nights = defaultCriteria.nights || null;
  }
  if (!tourists) {
    tourists = DEFAULT_TOURISTS;
  }

  if (params?.resorts) {
    destination = {
      country: { ...country, resorts },
      resorts: resorts.filter((r) => params.resorts?.includes(r.id)),
      kind: 'resorts',
    };
  } else if (params?.hotels) {
    const hotels = await findHotels({ country: country.id, hotels: params.hotels });
    destination = {
      kind: 'hotels',
      hotels,
      country: { ...country, resorts },
    };
  } else {
    destination = {
      kind: 'country',
      country: { ...country, resorts },
    };
  }

  return {
    dateRange,
    destination,
    tourists,
    nights,
    fromCity,
  };
}

export function buildSearchCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const countries: Country[] = [];
  const resorts: Resort[] = [];
  const hotels: Hotel[] = [];
  const { destination } = params;

  if (destination?.kind !== 'none' && destination?.country) {
    countries.push(destination?.country);
  }
  if (destination?.kind === 'resorts') {
    resorts.push(...destination.resorts);
  }
  if (destination?.kind === 'hotels') {
    hotels.push(...destination.hotels);
  }

  return {
    checkInDateRange: {
      from: params.dateRange?.from || '',
      to: params.dateRange?.to || '',
    },
    nightRange: {
      from: params.nights?.from || 0,
      to: params.nights?.to || 0,
    },
    departureCity: {
      id: params.fromCity?.id || 0,
      name: params.fromCity?.name || '',
    },
    touristGroup: toTouristGroup(params.tourists?.rooms),
    countries: countries.map(({ id }) => id),
    resorts: resorts.map(({ id }) => id),
    hotels: hotels.map(({ id }) => id),
  };
}
export function buildHistoryCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const historyParams = buildSearchCriteriaParams(params);

  if (!historyParams.resorts?.length) {
    delete historyParams.resorts;
  }
  if (!historyParams.hotels?.length) {
    delete historyParams.hotels;
  }

  const daysFlexible = params.dateRange?.daysFlexible;
  if (daysFlexible) {
    historyParams.checkInDateRange = { ...historyParams.checkInDateRange, daysFlexible };
  }

  return historyParams;
}

export function buildStorageCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const storageParams = buildHistoryCriteriaParams(params);

  delete storageParams.hotels;

  return storageParams;
}
